import React, { useState } from 'react'
import styled from 'styled-components'

import { up } from 'styled-breakpoints'

export type TabsCaption<T> = {
    title: string
    value: T
    activeColor?: string
}

type TabsProps<T> = {
    value: T
    captions: TabsCaption<T>[]
    onChange: (s: T) => void
    className?: string
}

const Tabs = <T,>({ value, captions, onChange, className }: TabsProps<T>) => {
    const [currentValue, setValue] = useState(value)

    const clickCallbackFor = (v: T) => {
        return () => {
            setValue(v)
            onChange(v)
        }
    }

    return (
        <Container className={className}>
            <Wrapper columns={captions.length}>
                {
                    captions.map(c => <Entry
                            active={c.value === currentValue}
                            activeColor={c.activeColor}
                            key={String(c.value)}
                            onClick={clickCallbackFor(c.value)}
                        >
                            {c.title}
                        </Entry>
                    )
                }
            </Wrapper>
        </Container>
    )
}

const Container = styled.div`
    position: relative;

    width: 100%;

    color: ${p => p.theme.colors.hushed};

    &::after {
        content: "";
        
        position: absolute;
        bottom: 0px;
        left: 0px;
        
        width: 100%;
        height: 1px;
        
        background: ${p => p.theme.colors.lightGray};
    }

    overflow-x: auto;
    overflow-y: visible;
`

const Wrapper = styled.div<{ columns: number }>`
    display: grid;
    width: fit-content;
    grid-template-columns: repeat(${p => p.columns}, auto);

    grid-gap: 32px;

    ${up('tablet')} {
        grid-gap: 40px;
    }
`

const Entry = styled.div<{ active: boolean, activeColor?: string }>`
    position: relative;

    white-space: nowrap;

    font-style: normal;
    font-weight: 600;
    font-size: 16px;
    line-height: 28px;

    ${p => p.active ?  `color: ${p.activeColor || p.theme.colors.accent};` : ''};

    padding: 14px 0;

    ${up('tablet')} {
        padding: 16px 0;
    }

    cursor: pointer;

    &::after {
        display: ${p => p.active ? 'default' : 'none'};
        content: "";
        
        position: absolute;
        bottom: -0px;
        left: -10px;
        
        width: calc(100% + 20px);
        height: 4px;
        
        background: ${p => p.activeColor || p.theme.colors.accent};
    }

    z-index: 10;
`

export default Tabs